/**
 * RateAssignmentTimeline - Visual timeline of rate periods for an entity
 * Shows current, past, and future rate assignments
 */

import { useState } from 'react';
import {
  Calendar,
  Clock,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { TodayDivider } from '@/components/ui/today-divider';
import type { RateAssignmentWithDetails } from '@/types/rates';
import { formatEffectivePeriod, formatRatePerKwh } from '@/types/rates';
import { parseISO, isAfter, isBefore, isToday, startOfDay } from 'date-fns';

interface RateAssignmentTimelineProps {
  assignments: RateAssignmentWithDetails[];
  onEdit?: (assignment: RateAssignmentWithDetails) => void;
  onDelete?: (assignment: RateAssignmentWithDetails) => void;
  isLoading?: boolean;
  showActions?: boolean;
}

type AssignmentStatus = 'current' | 'upcoming' | 'past';

function getAssignmentStatus(assignment: RateAssignmentWithDetails): AssignmentStatus {
  const today = startOfDay(new Date());
  const effectiveFrom = startOfDay(parseISO(assignment.effectiveFrom));
  const effectiveTo = assignment.effectiveTo
    ? startOfDay(parseISO(assignment.effectiveTo))
    : null;

  // Current: today is within the range
  if (
    (isToday(effectiveFrom) || isBefore(effectiveFrom, today)) &&
    (!effectiveTo || isAfter(effectiveTo, today) || isToday(effectiveTo))
  ) {
    return 'current';
  }

  // Upcoming: starts in the future
  if (isAfter(effectiveFrom, today)) {
    return 'upcoming';
  }

  // Past: ended before today
  return 'past';
}

function getStatusColor(status: AssignmentStatus): string {
  switch (status) {
    case 'current':
      return 'bg-[#8FB560]/20 text-[#8FB560] border border-[#8FB560]/30';
    case 'upcoming':
      return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
    case 'past':
      return 'bg-muted text-muted-foreground';
  }
}

function getStatusLabel(status: AssignmentStatus): string {
  switch (status) {
    case 'current':
      return 'Active';
    case 'upcoming':
      return 'Upcoming';
    case 'past':
      return 'Ended';
  }
}

export function RateAssignmentTimeline({
  assignments,
  onEdit,
  onDelete,
  isLoading = false,
  showActions = true,
}: RateAssignmentTimelineProps) {
  const [showPast, setShowPast] = useState(false);

  // Sort assignments by effective_from date (newest first)
  const sortedAssignments = [...assignments].sort((a, b) => {
    return new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime();
  });

  // Categorize assignments
  const currentAssignments = sortedAssignments.filter(
    a => getAssignmentStatus(a) === 'current'
  );
  const upcomingAssignments = sortedAssignments.filter(
    a => getAssignmentStatus(a) === 'upcoming'
  );
  const pastAssignments = sortedAssignments.filter(
    a => getAssignmentStatus(a) === 'past'
  );


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <div className="animate-pulse">Loading assignments...</div>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Calendar className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-muted-foreground">No rate assignments</p>
        <p className="text-xs text-muted-foreground mt-1">
          Rate will be inherited from parent hierarchy
        </p>
      </div>
    );
  }

  const renderAssignment = (assignment: RateAssignmentWithDetails) => {
    const status = getAssignmentStatus(assignment);
    const statusColor = getStatusColor(status);
    const statusLabel = getStatusLabel(status);

    return (
      <div
        key={assignment.id}
        className={cn(
          'relative pl-6 pb-6 last:pb-0',
          status === 'past' && 'opacity-60'
        )}
      >
        {/* Timeline line */}
        <div className="absolute left-[9px] top-[24px] bottom-0 w-[2px] bg-border last:hidden" />

        {/* Timeline dot */}
        <div
          className={cn(
            'absolute left-0 top-[6px] w-[20px] h-[20px] rounded-full flex items-center justify-center',
            status === 'current' && 'bg-[#8FB560]',
            status === 'upcoming' && 'bg-blue-500',
            status === 'past' && 'bg-muted border-2 border-border'
          )}
        >
          <Zap className={cn(
            'h-3 w-3',
            status === 'current' && 'text-white',
            status === 'upcoming' && 'text-white',
            status === 'past' && 'text-muted-foreground'
          )} />
        </div>

        {/* Content */}
        <div className="border border-border rounded-lg p-4 ml-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{assignment.rate.name}</span>
                <Badge className={cn('text-xs', statusColor)}>
                  {statusLabel}
                </Badge>
              </div>
              <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>
                  {formatEffectivePeriod(assignment.effectiveFrom, assignment.effectiveTo)}
                </span>
              </div>
            </div>

            {/* Actions */}
            {showActions && status !== 'past' && (
              <div className="flex gap-1">
                {onEdit && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onEdit(assignment)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {onDelete && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => onDelete(assignment)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>

          {/* Rate Details */}
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Import:</span>
              <span className="font-mono">
                {formatRatePerKwh(assignment.rate.gridImportRate)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Sell:</span>
              <span className="font-mono">
                {formatRatePerKwh(assignment.rate.gridSellRate)}
              </span>
            </div>
            {assignment.rate.utilityProvider && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Provider:</span>
                <span>{assignment.rate.utilityProvider}</span>
              </div>
            )}
            {assignment.rate.stateProvince && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">State:</span>
                <span>{assignment.rate.stateProvince}</span>
              </div>
            )}
          </div>

          {/* Override indicator for units */}
          {assignment.isOverride && (
            <Badge variant="outline" className="mt-2 text-xs">
              Override
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Newest first: upcoming above the Today line, active below it. The
          line is always drawn, and the drawer opens scrolled to it. */}
      {upcomingAssignments.length > 0 && (
        <div className="space-y-0">
          {upcomingAssignments.map(renderAssignment)}
        </div>
      )}
      <TodayDivider scrollIntoViewOnMount />
      {currentAssignments.length > 0 ? (
        <div className="space-y-0">
          {currentAssignments.map(renderAssignment)}
        </div>
      ) : upcomingAssignments.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-4">
          No active or upcoming rate assignments
        </div>
      ) : null}

      {/* Past Assignments (Collapsible) */}
      {pastAssignments.length > 0 && (
        <Collapsible open={showPast} onOpenChange={setShowPast}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between text-muted-foreground hover:text-foreground"
            >
              <span>Past Assignments ({pastAssignments.length})</span>
              {showPast ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-0 pt-2">
            {pastAssignments.map(renderAssignment)}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
